import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type FormEvent,
} from "react";
import maplibregl from "maplibre-gl";
import { marked } from "marked";
// CSS loaded via index.html CDN link
import "./index.css";

marked.setOptions({ breaks: true, gfm: true });

interface Candidate {
  id: number;
  address: string;
  lat: number;
  lon: number;
  score: number;
  sources: string[];
  details?: string;
}

function looksLikeUrl(text: string): boolean {
  return /^https?:\/\/.+\..+/.test(text.trim());
}

export function App() {
  const [url, setUrl] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("url") ?? "";
  });
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [hoveredCandidate, setHoveredCandidate] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const submitUrl = useCallback(async (targetUrl: string) => {
    const trimmed = targetUrl.trim();
    if (!trimmed) return;

    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;

    // Sync URL to address bar
    const params = new URLSearchParams(window.location.search);
    params.set("url", trimmed);
    window.history.replaceState(null, "", `?${params}`);

    setUrl(trimmed);
    setLoading(true);
    setOutput("");
    setError("");
    setCandidates([]);

    try {
      const res = await fetch("/api/locate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: trimmed }),
        signal: abort.signal,
      });

      if (!res.ok || !res.body) {
        setError("Erreur serveur");
        setLoading(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;

          try {
            const data = JSON.parse(payload);
            if (data.type === "text") {
              setOutput((prev) => prev + data.content);
            } else if (data.type === "candidate") {
              setCandidates((prev) =>
                [...prev, data.candidate].sort((a, b) => b.score - a.score)
              );
            } else if (typeof data === "string") {
              // Backward compat
              setOutput((prev) => prev + data);
            } else if (data.error) {
              setError(data.error);
            }
          } catch {}
        }
      }
    } catch (e: any) {
      if (e.name !== "AbortError") {
        console.error("[fetch error]", e);
        setError(`Erreur de connexion: ${e.message || e}`);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-submit if ?url= is present on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const initialUrl = params.get("url");
    if (initialUrl && looksLikeUrl(initialUrl)) {
      submitUrl(initialUrl);
    }
  }, [submitUrl]);

  useEffect(() => {
    function handlePaste(e: ClipboardEvent) {
      if (document.activeElement === inputRef.current) return;
      const text = e.clipboardData?.getData("text/plain") ?? "";
      if (looksLikeUrl(text)) {
        e.preventDefault();
        submitUrl(text);
      }
    }
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [submitUrl]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    submitUrl(url);
  }

  const hasResults = loading || output || error;
  const hasMap = candidates.length > 0;

  const searchBar = (
    <form onSubmit={handleSubmit}>
      <div className="relative bg-white border border-parchment rounded-sm shadow-[0_2px_20px_rgba(27,36,50,0.06)] transition-shadow duration-300 hover:shadow-[0_4px_30px_rgba(27,36,50,0.1)] focus-within:shadow-[0_4px_30px_rgba(196,123,90,0.12)]">
        <div className="flex items-center px-5 py-3 pl-6">
          <svg
            className="w-5 h-5 text-ink-muted shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
            />
          </svg>
          <input
            ref={inputRef}
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Collez l'URL de l'annonce..."
            className="flex-1 ml-3 outline-none text-ink text-[15px] bg-transparent placeholder:text-ink-muted/60 font-body"
            required
            autoFocus
          />
          <button
            type="submit"
            disabled={loading}
            className="ml-4 bg-ink hover:bg-ink-light disabled:opacity-40 text-cream text-[13px] font-500 tracking-wide uppercase px-5 py-2.5 rounded-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            {loading ? "Analyse..." : "Localiser"}
          </button>
        </div>
      </div>
    </form>
  );

  // Landing: centered vertically
  if (!hasResults) {
    return (
      <div className="relative min-h-screen flex flex-col items-center justify-center px-6">
        <GridExplorer active={false} />
        <div className="relative z-10 w-full max-w-xl flex flex-col items-center gap-10">
          <header className="text-center animate-fade-up">
            <h1 className="font-display text-5xl md:text-6xl font-300 text-ink tracking-tight leading-[1.1]">
              Localisateur
              <span className="text-copper">.</span>
              <span className="italic font-display text-copper">immo</span>
            </h1>
            <p className="mt-4 text-ink-muted text-[15px] leading-relaxed max-w-md mx-auto">
              Collez le lien d'une annonce immobilière pour obtenir
              une analyse détaillée du bien et de sa localisation.
            </p>
          </header>
          <div className="w-full animate-fade-up" style={{ animationDelay: "0.15s" }}>
            {searchBar}
            <p className="mt-4 text-center text-[12px] tracking-wide text-ink-muted/70 uppercase">
              LeBonCoin &middot; SeLoger &middot; PAP &middot; Bien'ici &middot;
              Logic-Immo
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Results layout
  return (
    <div className="relative min-h-screen flex flex-col">
      <GridExplorer active={loading} />

      {/* Header — compact */}
      <header className="relative z-10 text-center pt-6 pb-3">
        <h1 className="font-display text-3xl md:text-4xl font-300 text-ink tracking-tight leading-[1.1]">
          Localisateur
          <span className="text-copper">.</span>
          <span className="italic font-display text-copper">immo</span>
        </h1>
      </header>

      {/* Search bar — compact */}
      <div className="relative z-10 mx-auto w-full max-w-xl px-6 pb-4">
        {searchBar}
      </div>

      {/* Results: split layout when map is active */}
      <div
          className={`relative z-10 flex-1 flex ${hasMap ? "flex-row" : "flex-col items-center"} gap-0 min-h-0`}
        >
          {/* Left panel: text + candidates */}
          <div
            className={`${hasMap ? "w-1/2 border-r border-parchment" : "w-full max-w-xl mx-auto px-6"} overflow-y-auto`}
          >
            <div className="p-6">
              {/* Loading bar */}
              {loading && !output && (
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-2 h-2 rounded-full bg-copper animate-pulse" />
                  <p className="text-ink-muted text-sm">
                    Analyse de l'annonce en cours...
                  </p>
                </div>
              )}

              {error && (
                <p className="text-error text-sm">{error}</p>
              )}

              {output && (
                <div className="bg-white border border-parchment rounded-sm p-5">
                  {loading && (
                    <div className="relative h-[2px] overflow-hidden mb-4 -mx-5 -mt-5 rounded-t-sm">
                      <div className="absolute inset-0 animate-scan bg-gradient-to-r from-transparent via-copper to-transparent" />
                    </div>
                  )}
                  <div
                    className="markdown-body text-ink font-body text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: marked.parse(output) as string }}
                  />
                </div>
              )}

              {/* Candidate list */}
              {candidates.length > 0 && (
                <div className="mt-6 pt-4 border-t border-parchment">
                  <h3 className="font-display text-base text-ink mb-3">
                    Adresses candidates
                  </h3>
                  <div className="space-y-2">
                    {candidates.map((c, i) => (
                      <div
                        key={c.id}
                        className={`p-3 rounded-sm border transition-colors cursor-pointer ${
                          hoveredCandidate === c.id
                            ? "border-copper bg-copper/5"
                            : "border-parchment bg-white"
                        }`}
                        onMouseEnter={() => setHoveredCandidate(c.id)}
                        onMouseLeave={() => setHoveredCandidate(null)}
                      >
                        <div className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-copper text-cream text-xs font-600 flex items-center justify-center">
                            {i + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-500 text-ink truncate">
                              {c.address}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              {/* Score bar */}
                              <div className="flex-1 h-1.5 bg-parchment rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-copper rounded-full transition-all"
                                  style={{ width: `${c.score * 100}%` }}
                                />
                              </div>
                              <span className="text-[11px] text-ink-muted font-500">
                                {Math.round(c.score * 100)}%
                              </span>
                            </div>
                            <div className="flex gap-1 mt-1.5">
                              {c.sources.map((s) => (
                                <span
                                  key={s}
                                  className="text-[10px] uppercase tracking-wider text-ink-muted bg-parchment/60 px-1.5 py-0.5 rounded-sm"
                                >
                                  {s}
                                </span>
                              ))}
                            </div>
                            {c.details && (
                              <p className="text-[12px] text-ink-muted mt-1">
                                {c.details}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right panel: map */}
          {hasMap && (
            <div className="w-1/2 sticky top-0 self-start h-screen">
              <CandidateMap
                candidates={candidates}
                hoveredCandidate={hoveredCandidate}
                onHover={setHoveredCandidate}
              />
            </div>
          )}
        </div>
    </div>
  );
}

function buildPopupHtml(c: Candidate, rank: number): string {
  const q = `${c.lat},${c.lon}`;
  return `
    <div style="font-family: 'DM Sans', sans-serif; font-size: 13px; line-height: 1.5; min-width: 200px;">
      <div style="font-weight: 600; font-size: 14px; color: #1B2432; margin-bottom: 4px;">
        ${c.address}
      </div>
      <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 8px;">
        <span style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #8A9AB5;">
          ${c.sources.join(" · ")}
        </span>
        <span style="margin-left: auto; font-size: 12px; font-weight: 500; color: #9B4D3A;">
          ${Math.round(c.score * 100)}%
        </span>
      </div>
      ${c.details ? `<div style="font-size: 12px; color: #3D4A5C; margin-bottom: 8px;">${c.details}</div>` : ""}
      <div style="border-top: 1px solid #EDE8DE; padding-top: 8px; font-size: 12px;">
        <div style="color: #8A9AB5; text-transform: uppercase; letter-spacing: 0.05em; font-size: 10px; margin-bottom: 4px;">
          Ouvrir dans
        </div>
        <div style="display: flex; flex-wrap: wrap; gap: 4px 8px;">
          <a href="https://www.google.com/maps/search/?api=1&query=${q}" target="_blank" rel="noopener"
             style="color: #9B4D3A; text-decoration: none;">Google Maps</a>
          <a href="https://maps.apple.com/?ll=${q}&z=18&q=${encodeURIComponent(c.address)}" target="_blank" rel="noopener"
             style="color: #9B4D3A; text-decoration: none;">Apple Plans</a>
          <a href="https://www.bing.com/maps?cp=${q.replace(",", "~")}&lvl=18" target="_blank" rel="noopener"
             style="color: #9B4D3A; text-decoration: none;">Bing</a>
          <a href="https://www.geoportail.gouv.fr/carte?c=${c.lon},${c.lat}&z=18" target="_blank" rel="noopener"
             style="color: #9B4D3A; text-decoration: none;">Géoportail</a>
        </div>
      </div>
    </div>`;
}

const CADASTRE_WMS_URL =
  "https://data.geopf.fr/wms-v/ows?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&LAYERS=CADASTRALPARCELS.PARCELLAIRE_EXPRESS&CRS=EPSG:3857&STYLES=&FORMAT=image/png&TRANSPARENT=true&WIDTH=256&HEIGHT=256&BBOX={bbox-epsg-3857}";

function CandidateMap({
  candidates,
  hoveredCandidate,
  onHover,
}: {
  candidates: Candidate[];
  hoveredCandidate: number | null;
  onHover: (id: number | null) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<Map<number, maplibregl.Marker>>(new Map());

  // Initialize map with cadastral layer
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
      center: [2.35, 46.85],
      zoom: 5,
    });

    map.addControl(new maplibregl.NavigationControl(), "top-right");

    map.on("load", () => {
      // Cadastral parcels overlay from IGN
      map.addSource("cadastre", {
        type: "raster",
        tiles: [CADASTRE_WMS_URL],
        tileSize: 256,
      });
      map.addLayer({
        id: "cadastre-layer",
        type: "raster",
        source: "cadastre",
        paint: { "raster-opacity": 0.4 },
        minzoom: 14,
      });
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Update markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current.clear();

    if (candidates.length === 0) return;

    const sorted = [...candidates].sort((a, b) => b.score - a.score);
    const bounds = new maplibregl.LngLatBounds();

    sorted.forEach((c, i) => {
      const el = document.createElement("div");
      el.textContent = String(i + 1);
      const isHovered = hoveredCandidate === c.id;
      el.style.cssText = `
        width: 28px; height: 28px; border-radius: 50%;
        background: ${isHovered ? "#1B2432" : "#9B4D3A"};
        color: #FAF7F2; font-size: 13px; font-weight: 600;
        display: flex; align-items: center; justify-content: center;
        cursor: pointer; border: 2px solid #FAF7F2;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        transition: background 0.2s, transform 0.2s;
        ${isHovered ? "transform: scale(1.2);" : ""}
      `;

      el.addEventListener("mouseenter", () => onHover(c.id));
      el.addEventListener("mouseleave", () => onHover(null));

      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([c.lon, c.lat])
        .setPopup(
          new maplibregl.Popup({ offset: 20, maxWidth: "320px" }).setHTML(
            buildPopupHtml(c, i + 1)
          )
        )
        .addTo(map);

      markersRef.current.set(c.id, marker);
      bounds.extend([c.lon, c.lat]);
    });

    map.fitBounds(bounds, { padding: 60, maxZoom: 17 });
  }, [candidates, hoveredCandidate, onHover]);

  return <div ref={containerRef} style={{ width: "100%", height: "100%" }} />;
}

const GRID_SIZE = 80;

function GridExplorer({ active }: { active: boolean }) {
  const cells = useMemo(() => {
    const pool: {
      col: number;
      row: number;
      delay: number;
      duration: number;
    }[] = [];
    for (let i = 0; i < 18; i++) {
      pool.push({
        col: Math.floor(Math.random() * 25),
        row: Math.floor(Math.random() * 15),
        delay: Math.random() * 4,
        duration: 1.5 + Math.random() * 2.5,
      });
    }
    return pool;
  }, []);

  if (!active) return null;

  return (
    <div className="fixed inset-0 z-1 pointer-events-none animate-fade-in">
      {cells.map((cell, i) => (
        <div
          key={i}
          className="absolute animate-grid-pulse rounded-[1px]"
          style={{
            left: cell.col * GRID_SIZE + 1,
            top: cell.row * GRID_SIZE + 1,
            width: GRID_SIZE - 2,
            height: GRID_SIZE - 2,
            animationDelay: `${cell.delay}s`,
            animationDuration: `${cell.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

export default App;
